// [목적] /  (guest 진입) 랜딩 페이지 — Header / Hero / Trust / Features / How / Gallery / Reviews / FAQ / Final CTA / Footer 10 섹션
// [이유] docs/플리오랩/강원캔버스-웹.html 1:1 포팅. RootSwitch 가 미인증 사용자에게 본 페이지를 노출하여
//         App 셸(Sidebar/BottomTabBar) 우회 + OAuth 전환 동선("웹에서 시작" CTA → /login) 일관성 확보.
// [사이드 이팩트] App 셸 미사용 — 자체 Header/Footer 만 사용. 모든 hex/그림자/그라데이션은
//                 pages/landing/tokens.ts 의 LANDING_COLORS / LANDING_SHADOWS / LANDING_GRADIENTS 경유.
//                 본 파일에는 hex/px 직접 입력 0건 (예외: tokens.ts SoT 만 허용).
//                 nav-cta · hero btn-primary · final-cta primary · store/foot 일부 링크는 /login 으로 navigate.
// [수정 시 고려사항] 시각 회귀가 우선이므로 HTML 원본의 spacing/font-size 비율을 유지할 것.
//                   packages/ui 토큰 확장(brand 2/3, shadow 3단) 진입 시 tokens.ts import 경로만 교체.
//                   FAQ 는 button+aria-expanded 아코디언(QA P3-01) — MUI Accordion 은 미도입(번들 절약).
//                   목적지 없는 '#' 링크는 두지 않는다(QA P2-10) — 연결할 URL 이 생기기 전까지는 링크 자체를 만들지 않는다.

import {
  Box,
  ButtonBase,
  GlobalStyles,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  LANDING_COLORS as C,
  LANDING_GRADIENTS as G,
  LANDING_KEYFRAMES as K,
  LANDING_LAYOUT as LY,
  LANDING_SHADOWS as S,
} from './landing/tokens.js';

const radius = tokens.radius;
const fontMono = tokens.typography.fontFamily.mono;

// ─────────────────────────────────────────────────────────────────────────────
// 공통 유틸
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] 모든 section 공통 패딩 (데스크톱 100px / 모바일 64px)
 * [이유] HTML `section { padding: 100px 0 }` / `@media (max-width: 768px) { padding: 64px 0 }`
 * [사이드 이팩트] LandingPage 내부에서만 재사용
 * [수정 시 고려사항] HTML 768px → MUI md(900px) 로 매핑. 실측 시각 회귀 시 sm(600) 으로 조정
 */
const sectionSx: SxProps<Theme> = {
  py: { xs: `${LY.sectionPyMobile}px`, md: `${LY.sectionPyDesktop}px` },
};

const containerSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: `${LY.containerMax}px`,
  mx: 'auto',
  px: `${LY.containerPx}px`,
};

/**
 * [목적] 섹션 이거브로우(FEATURES/GALLERY/FAQ 등) 공통 스타일
 * [이유] HTML .section-eyebrow 1:1. brand(#1F6FEB) 는 12px 텍스트가 paper[1] 배경 위 4.4:1 로
 *        WCAG AA(4.5:1) 미달이었다(docs/qa/2026-09-17_contrast-measure.md) — brandStrong(brand[600])
 *        으로 교체해 6.36:1 확보.
 * [사이드 이팩트] 모든 sectionEyebrowSx 사용처(FEATURES/HOW/GALLERY/REVIEWS/FAQ) 색 일괄 변경.
 * [수정 시 고려사항] brandStrong 값은 packages/ui brand[600] 단일 소스 — 재하향 시 contrast.spec.ts 게이트 확인.
 */
const sectionEyebrowSx: SxProps<Theme> = {
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 700,
  color: C.brandStrong,
  letterSpacing: '2px',
  textTransform: 'uppercase',
  mb: '14px',
};

const sectionTitleSx: SxProps<Theme> = {
  textAlign: 'center',
  fontSize: 'clamp(28px, 3.5vw, 42px)',
  fontWeight: 800,
  letterSpacing: '-1px',
  lineHeight: 1.2,
  mb: '16px',
  color: C.ink,
};

const sectionSubSx: SxProps<Theme> = {
  textAlign: 'center',
  fontSize: 15,
  color: C.inkMid,
  maxWidth: '580px',
  mx: 'auto',
  mb: '60px',
  lineHeight: 1.6,
};

// ─────────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] 상단 고정 헤더 — 로고 + 네비 4개 + 우측 CTA
 * [이유] HTML <header class="header"> 섹션. fixed top, backdrop-filter blur.
 * [사이드 이팩트] 우측 CTA 클릭 시 /login 으로 navigate. nav 앵커는 동일 페이지 내 스크롤.
 * [수정 시 고려사항] body 첫 section 의 top padding 이 header 높이(64px) + hero 추가 padding 으로 보정됨.
 */
function LandingHeader(): JSX.Element {
  const navigate = useNavigate();
  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        bgcolor: C.whiteAlpha85,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${C.borderSoft}`,
      }}
    >
      <Box sx={containerSx}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ height: `${LY.headerHeight}px` }}
        >
          <Box
            component="a"
            href="/"
            aria-label="강원캔버스 홈"
            // [수정: QA-P2-10] 목적지 없는 href="#" → 홈. 이미 랜딩(/)이므로 페이지 맨 위로 부드럽게 이동한다.
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '9px',
                background: G.logoMark,
                color: C.paper,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 14,
                letterSpacing: '-.5px',
              }}
            >
              강
            </Box>
            <Typography
              sx={{ fontSize: 15, fontWeight: 800, letterSpacing: '-.4px', color: C.ink }}
            >
              강원캔버스
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" sx={{ gap: { xs: 0, sm: '28px' } }}>
            {(['features', 'how', 'gallery', 'faq'] as const).map((id) => {
              const label = { features: '기능', how: '사용법', gallery: '갤러리', faq: 'FAQ' }[id];
              return (
                <Box
                  key={id}
                  component="a"
                  href={`#${id}`}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    fontSize: 13,
                    color: C.inkMid,
                    fontWeight: 600,
                    transition: 'color .15s',
                    '&:hover': { color: C.ink },
                  }}
                >
                  {label}
                </Box>
              );
            })}
            <ButtonBase
              onClick={() => navigate('/login')}
              aria-label="웹에서 시작"
              sx={{
                bgcolor: C.ink,
                color: C.paper,
                px: '16px',
                py: '9px',
                borderRadius: '9px',
                fontSize: 12.5,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                '&:hover': { bgcolor: C.phoneBodyHover },
              }}
            >
              <span>→</span>
              <span>웹에서 시작</span>
            </ButtonBase>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero (phone mockup 포함)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] Hero 우측 폰 목업 — 챗 흐름 미리보기
 * [이유] HTML .phone (3D rotate) + .chat-bubble + .chat-card
 * [사이드 이팩트] 280x580 고정 — 모바일 viewport 에서는 hero-inner 1열 그리드로 폰이 아래로 내려감.
 *                [FlowLine 83963a6b 과업2] 컨테이너에 aria-hidden="true" 추가 — 내부는 전부 장식용
 *                시각 미리보기(실제 채팅 아님)라 스크린리더 탐색 대상에서 제외한다.
 * [수정 시 고려사항] perspective rotate 는 prefers-reduced-motion 대응 별도 hook 진입 시 끄기.
 *                   aria-hidden 은 스크린리더만 가리고 키보드 포커스는 막지 않으므로, 내부의
 *                   포커스 가능한 요소(예: 실제 button/a)는 별도로 비인터랙티브 요소로 바꿔야 한다
 *                   (아래 "폴리오 생성하기 ✦" 장식 버튼 참조).
 */
function HeroPhone(): JSX.Element {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: `${LY.phoneWidth}px`,
        height: `${LY.phoneHeight}px`,
        bgcolor: C.phoneBody,
        borderRadius: '38px',
        p: '8px',
        boxShadow: S.phone,
        transform: 'perspective(1500px) rotateY(-8deg) rotateX(2deg)',
        transition: 'transform .3s',
        '&:hover': { transform: 'perspective(1500px) rotateY(-4deg) rotateX(0deg)' },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          bgcolor: C.bg,
          borderRadius: '30px',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* notch */}
        <Box
          sx={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 70,
            height: 22,
            bgcolor: C.phoneBody,
            borderRadius: '12px',
            zIndex: 5,
          }}
        />
        {/* status bar */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ height: 40, px: '22px', pt: '12px', fontSize: 11, fontWeight: 700, color: C.ink }}
        >
          <span>9:30</span>
          <span>📶 100%</span>
        </Stack>
        {/* body */}
        <Box sx={{ flex: 1, px: '14px', pt: '8px', pb: '14px', overflow: 'hidden' }}>
          <Typography
            sx={{
              fontSize: 10,
              color: C.inkLow,
              fontFamily: fontMono,
              letterSpacing: '.4px',
              mb: '12px',
              px: '4px',
            }}
          >
            AI 컨셉 디자이너
          </Typography>
          <ChatBubble>
            안녕하세요! 어떤
            <br />
            강원 여행을 그려볼까요? ✦
          </ChatBubble>
          <ChatBubble me>
            친구 2명, 반려견과
            <br />
            2박3일 속초·양양
          </ChatBubble>
          <ChatBubble>좋아요! 이대로 만들어볼게요.</ChatBubble>
          <ChatCard />
          {/* [목적] 폰 목업 내부 장식 CTA — 실제 동작 없는 시각 미리보기
              [이유] FlowLine 83963a6b 과업2: component="button" 은 onClick 없이도 진짜 <button>
              엘리먼트라 Tab 으로 포커스되는 "빈 상호작용" 트랩이었다(aria-hidden 은 스크린리더만
              가리고 키보드 포커스는 막지 않음, WCAG 2.4.3/4.1.2). span(비인터랙티브)으로 교체해
              탭 순서에서 완전히 제외 — 시각적 스타일은 그대로 유지.
              [사이드 이팩트] 없음(렌더 출력 동일, DOM 엘리먼트 태그만 button→span).
              [수정 시 고려사항] 실제 클릭 동작이 필요해지면 button 복원 + 실제 onClick 핸들러를
              함께 추가해야 함(현재는 순수 장식). */}
          <Box
            component="span"
            sx={{
              mt: '6px',
              background: G.chatCta,
              color: C.paper,
              border: 'none',
              borderRadius: '10px',
              py: '9px',
              fontSize: 11,
              fontWeight: 700,
              width: '100%',
              display: 'block',
              textAlign: 'center',
            }}
          >
            폴리오 생성하기 ✦
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * [목적] 챗 말풍선 (내 말풍선 / 봇 말풍선 분기)
 * [이유] HTML .chat-bubble / .chat-bubble.me 1:1
 * [사이드 이팩트] children: ReactNode — <br/> 등 인라인 요소 허용
 * [수정 시 고려사항] 모서리 곡률(13px/4px)·max-width(80%)는 폰 목업 비율 기준 — 폰 width 변경 시 재검수
 */
function ChatBubble({
  children,
  me = false,
}: {
  children: React.ReactNode;
  me?: boolean;
}): JSX.Element {
  return (
    <Box
      sx={{
        bgcolor: me ? undefined : C.paper,
        background: me ? G.chatBubbleMe : undefined,
        color: me ? C.paper : C.ink,
        borderRadius: me ? '13px 13px 4px 13px' : '4px 13px 13px 13px',
        p: '9px 12px',
        fontSize: 11,
        lineHeight: 1.5,
        boxShadow: me ? 'none' : S.s1,
        mb: '8px',
        maxWidth: '80%',
        ml: me ? 'auto' : 0,
      }}
    >
      {children}
    </Box>
  );
}

/**
 * [목적] 'AI가 파악한 여행' 카드 — 4행 (동반자/기간/예산/무드)
 * [이유] HTML .chat-card
 * [사이드 이팩트] 폰 body 내부에서만 사용
 * [수정 시 고려사항] 행이 5개 이상으로 늘면 폰 height(580px) 내부 overflow — 폰 크기/스크롤 분리 검토
 */
function ChatCard(): JSX.Element {
  const rows: Array<[string, string]> = [
    ['동반자', '친구 + 반려견'],
    ['기간', '2박 3일'],
    ['예산', '1인 30만원'],
    ['무드', '바다·카페'],
  ];
  return (
    <Box
      sx={{
        bgcolor: C.paper,
        borderRadius: '11px',
        p: '10px 12px',
        mt: '6px',
        mb: '8px',
        border: `1px solid ${C.border}`,
      }}
    >
      <Typography
        sx={{
          fontSize: 9,
          color: C.brand,
          fontWeight: 700,
          letterSpacing: '.4px',
          mb: '6px',
        }}
      >
        AI가 파악한 여행
      </Typography>
      {rows.map(([k, v]) => (
        <Stack
          key={k}
          direction="row"
          justifyContent="space-between"
          sx={{ fontSize: 10, py: '2px' }}
        >
          <span style={{ color: C.inkLow }}>{k}</span>
          <span style={{ color: C.ink, fontWeight: 600 }}>{v}</span>
        </Stack>
      ))}
    </Box>
  );
}

/**
 * [목적] Hero 섹션 전체 — 좌(eyebrow/h1/lead/CTA/stats) + 우(폰 목업 + 부유 칩 3개)
 * [이유] HTML <section class="hero">
 * [사이드 이팩트] 데스크톱 2열 / md 미만 1열. CTA primary → /login, secondary → #how
 * [수정 시 고려사항] em(브랜드 밑줄) 의 background gradient 는 LANDING_COLORS.brandUnderline 경유
 */
function HeroSection(): JSX.Element {
  const navigate = useNavigate();
  return (
    <Box
      component="section"
      sx={{
        pt: { xs: '120px', md: `${LY.heroPtDesktop}px` },
        pb: { xs: '64px', md: `${LY.heroPbDesktop}px` },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: G.heroRadial,
        }}
      />
      <Box sx={{ ...containerSx, position: 'relative' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: '48px', md: '64px' },
            alignItems: 'center',
          }}
        >
          {/* 좌측 텍스트 */}
          <Box>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                bgcolor: C.paper,
                border: `1px solid ${C.border}`,
                px: '13px',
                py: '7px',
                borderRadius: '99px',
                fontSize: 11.5,
                fontWeight: 600,
                color: C.inkMid,
                mb: '24px',
                boxShadow: S.s1,
                '& b': { color: C.brand, fontWeight: 700 },
              }}
            >
              <span>✦</span>
              <span>
                <b>2025 관광데이터 활용 공모전</b> 출품작
              </span>
            </Box>

            <Typography
              component="h1"
              sx={{
                fontSize: 'clamp(34px, 5vw, 56px)',
                fontWeight: 800,
                letterSpacing: '-1.5px',
                lineHeight: 1.12,
                mb: '22px',
                color: C.ink,
              }}
            >
              대화 한 줄로
              <br />
              <Box
                component="em"
                sx={{
                  fontStyle: 'normal',
                  color: C.brand,
                  background: `linear-gradient(180deg, transparent 65%, ${C.brandUnderline} 65%)`,
                  px: '4px',
                }}
              >
                강원 여행
              </Box>
              이<br />
              그려져요
            </Typography>

            <Typography
              sx={{
                fontSize: 17,
                color: C.inkMid,
                lineHeight: 1.65,
                mb: '30px',
                maxWidth: '480px',
              }}
            >
              반려견과 갈 카페, 친구들과 떠날 1박 2일, 조용한 카페와 바다 산책 — 취향을 말하면
              강원캔버스가 일정을 그려드려요.
            </Typography>

            <Stack direction="row" sx={{ gap: '10px', flexWrap: 'wrap' }}>
              <ButtonBase
                onClick={() => navigate('/login')}
                aria-label="웹에서 바로 시작"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  bgcolor: C.brand,
                  color: C.paper,
                  px: '22px',
                  py: '13px',
                  borderRadius: '11px',
                  fontSize: 14,
                  fontWeight: 700,
                  boxShadow: S.primaryBtn,
                  transition: 'transform .15s, box-shadow .15s',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: S.primaryBtnHover,
                  },
                }}
              >
                <span>→</span>
                <span>웹에서 바로 시작</span>
              </ButtonBase>
              <Box
                component="a"
                href="#how"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  bgcolor: C.paper,
                  color: C.ink,
                  px: '22px',
                  py: '13px',
                  borderRadius: '11px',
                  fontSize: 14,
                  fontWeight: 700,
                  border: `1px solid ${C.border}`,
                  '&:hover': { bgcolor: C.bgSoft },
                }}
              >
                <span>▶</span>
                <span>1분 데모 보기</span>
              </Box>
            </Stack>

            <Box
              sx={{
                mt: '14px',
                fontSize: 11.5,
                color: C.inkLow,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.green }} />
              다운로드 불필요 · 브라우저에서 바로 · 웹·앱 자동 동기화
            </Box>

            <Stack
              direction="row"
              sx={{
                gap: '32px',
                mt: '40px',
                pt: '30px',
                borderTop: `1px solid ${C.borderSoft}`,
                flexWrap: 'wrap',
              }}
            >
              {/* [수정: 디자인 개편] 검증되지 않은 수치(5분 평균 생성 시간, 1,200+ 베타 사용자)를 제거하고
                  실제로 확인 가능한 사실만 남긴다. */}
              {(
                [
                  ['18', '강원 시·군 전체 커버'],
                  ['4.0', 'TourAPI 기반 데이터'],
                ] as const
              ).map(([num, lbl]) => (
                <Box key={lbl}>
                  <Typography
                    sx={{
                      fontSize: 24,
                      fontWeight: 800,
                      letterSpacing: '-.5px',
                      fontFamily: fontMono,
                      color: C.ink,
                    }}
                  >
                    {num}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: C.inkLow, fontWeight: 600, mt: '3px' }}>
                    {lbl}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* 우측 폰 + 부유 칩 */}
          <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
            <FloatChip
              emoji="🐾"
              label="반려견과 함께"
              pos={{ top: '60px', left: '-10%', delay: '0s', mobileLeft: '5%' }}
            />
            <FloatChip
              emoji="☕"
              label="카페 위주로"
              pos={{ top: '30%', right: '-15%', delay: '1.2s', mobileRight: '5%' }}
            />
            <FloatChip
              emoji="🚄"
              label="KTX 강릉"
              pos={{ bottom: '100px', left: '-5%', delay: '2.4s', mobileLeft: '8%' }}
            />
            <HeroPhone />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * [목적] 부유 칩 (3개) — 폰 주변 떠다니는 미니 카드
 * [이유] HTML .float-chip + @keyframes float
 * [사이드 이팩트] animation: landing-float — GlobalStyles 로 keyframes 주입됨
 * [수정 시 고려사항] prefers-reduced-motion 사용자 대응 — useReducedMotion 훅 도입 시 animation 무력화
 */
function FloatChip({
  emoji,
  label,
  pos,
}: {
  emoji: string;
  label: string;
  pos: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    delay: string;
    mobileLeft?: string;
    mobileRight?: string;
  };
}): JSX.Element {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: pos.top,
        bottom: pos.bottom,
        left: { xs: pos.mobileLeft ?? pos.left, md: pos.left },
        right: { xs: pos.mobileRight ?? pos.right, md: pos.right },
        bgcolor: C.paper,
        border: `1px solid ${C.border}`,
        borderRadius: '10px',
        py: '8px',
        pl: '10px',
        pr: '12px',
        fontSize: 12,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        boxShadow: S.s2,
        color: C.ink,
        animation: `landing-float 4s ease-in-out infinite`,
        animationDelay: pos.delay,
      }}
    >
      <span style={{ fontSize: 16 }}>{emoji}</span>
      <span>{label}</span>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Trust bar
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] 활용 데이터 신뢰 바
 * [이유] HTML <section class="trust">
 * [사이드 이팩트] 별도 CTA 없음
 * [수정 시 고려사항] 로고는 텍스트 placeholder — 실 이미지 도입 시 logos 배열을 {src, alt} 구조로 변경
 */
function TrustSection(): JSX.Element {
  const logos = ['한국관광공사 TourAPI 4.0', '무장애관광정보', '반려동물 동반여행', '강원관광재단'];
  return (
    <Box
      component="section"
      sx={{
        py: '36px',
        bgcolor: C.paper,
        borderTop: `1px solid ${C.borderSoft}`,
        borderBottom: `1px solid ${C.borderSoft}`,
      }}
    >
      <Box sx={containerSx}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{ gap: '36px', flexWrap: 'wrap' }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: C.inkLow,
              letterSpacing: '1px',
              textTransform: 'uppercase',
            }}
          >
            활용 데이터
          </Typography>
          {logos.map((l) => (
            <Typography
              key={l}
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: C.inkMid,
                letterSpacing: '-.3px',
                // 2026-09-17 디자인 리뷰: rest 0.65(3.1:1) → 0.85(4.9:1, WCAG AA) — hover 는 마우스 전용이라 기본 상태 기준
                opacity: 0.85,
                transition: 'opacity .15s',
                '&:hover': { opacity: 1 },
              }}
            >
              {l}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Features
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] 기능 6카드 그리드
 * [이유] HTML <section id="features">
 * [사이드 이팩트] 카드 hover lift — translateY(-4px) + shadow s3
 * [수정 시 고려사항] 카드 수 변경 시 md 3열 그리드 균형(2x3) 깨짐 — 4/6/9 개 단위 유지 권장
 */
function FeaturesSection(): JSX.Element {
  const items: Array<{ icon: string; bg: string; title: string; body: string }> = [
    {
      icon: '💬',
      bg: G.featureBgBlue,
      title: '대화로 시작',
      body: '"친구 둘, 반려견과 조용한 카페" 자연어 한 줄이면 충분해요. AI가 동반자·일정·무드를 파악합니다.',
    },
    {
      icon: '🗺',
      bg: G.featureBgGreen,
      title: '동선까지 그려서',
      body: '장소만 나열하지 않아요. 시간·거리·예산을 풀어서 실제로 다닐 수 있는 동선으로 묶어드려요.',
    },
    {
      icon: '📔',
      bg: G.featureBgWarm,
      title: '매거진형 폴리오',
      body: '커버부터 Day 카드, 지도, 무드보드까지. 한 장으로 보기 좋고 PDF·이미지·링크로 공유하기 쉬워요.',
    },
    {
      icon: '🐾',
      bg: G.featureBgPurple,
      title: '관광 약자 포용',
      body: '반려견·무장애·다국어를 부가 기능이 아닌 1차 필터로. 모두가 편안한 강원 여행을 만들어요.',
    },
    {
      icon: '⑂',
      bg: G.featureBgRed,
      title: 'Fork & Remix',
      body: '마음에 드는 폴리오는 그대로 가져와서 내 일정으로 수정. 일정 데이터가 재사용 가능한 자산이 됩니다.',
    },
    {
      icon: '🛡',
      bg: G.featureBgGray,
      title: '믿을 수 있는 정보',
      body: '장소·주소·전화는 한국관광공사 OpenAPI 원본을 인용. AI 환각은 RAG로 차단합니다.',
    },
  ];
  return (
    <Box component="section" id="features" sx={sectionSx}>
      <Box sx={containerSx}>
        <Typography sx={sectionEyebrowSx}>FEATURES</Typography>
        <Typography component="h2" sx={sectionTitleSx}>
          여행 계획, 더 이상 부담스럽지 않게
        </Typography>
        <Typography sx={sectionSubSx}>
          엑셀, 메모장, 인스타 저장에 흩어진 정보를 한 장의 폴리오로.
          <br />
          취향을 말하면 강원캔버스가 동선과 예산을 맞춰드려요.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: '16px', md: '20px' },
          }}
        >
          {items.map((it) => (
            <Box
              key={it.title}
              sx={{
                bgcolor: C.paper,
                border: `1px solid ${C.border}`,
                borderRadius: `${radius.l}px`,
                p: '28px',
                transition: 'transform .2s, box-shadow .2s, border-color .2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: S.s3,
                  borderColor: C.borderSoft,
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: it.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  mb: '18px',
                }}
              >
                {it.icon}
              </Box>
              <Typography
                component="h3"
                sx={{
                  fontSize: 18,
                  fontWeight: 800,
                  letterSpacing: '-.4px',
                  mb: '8px',
                  color: C.ink,
                }}
              >
                {it.title}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: C.inkMid, lineHeight: 1.65 }}>
                {it.body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// How it works
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] 3단계 사용법 — 챗 / 카드 리스트 / 공유 타일
 * [이유] HTML <section id="how" class="how-section">
 * [사이드 이팩트] 각 step visual 은 inline 미니 컴포넌트
 * [수정 시 고려사항] step 수 변경 시 md 3열 그리드 + step-num('STEP 0X') 라벨 일관성 동시 검토
 */
function HowSection(): JSX.Element {
  return (
    <Box
      component="section"
      id="how"
      sx={{
        ...sectionSx,
        bgcolor: C.paper,
        borderTop: `1px solid ${C.borderSoft}`,
        borderBottom: `1px solid ${C.borderSoft}`,
      }}
    >
      <Box sx={containerSx}>
        <Typography sx={sectionEyebrowSx}>HOW IT WORKS</Typography>
        <Typography component="h2" sx={sectionTitleSx}>
          3단계면 충분해요
        </Typography>
        <Typography sx={sectionSubSx}>대화부터 완성된 폴리오까지, 평균 5분 이내.</Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: '24px', md: '32px' },
          }}
        >
          <StepCard
            num="STEP 01"
            title="대화로 취향을 말하기"
            body="동반자·일정·무드를 자유롭게 적어주세요. 부족한 정보는 AI가 되묻습니다."
          >
            <Stack sx={{ gap: '6px', width: '100%' }}>
              <StepBubble>어떤 강원 여행을 그려볼까요?</StepBubble>
              <StepBubble me>2박 3일 반려견과</StepBubble>
              <StepBubble>예산은 어느 정도가 좋을까요?</StepBubble>
              <StepBubble me>1인 30만원 이내로</StepBubble>
            </Stack>
          </StepCard>

          <StepCard
            num="STEP 02"
            title="AI가 폴리오를 그려요"
            body="TourAPI 데이터로 후보를 찾고, 동선과 예산을 풀어 실제 일정으로 만듭니다."
          >
            <Stack sx={{ gap: '6px', width: '100%' }}>
              {(
                [
                  ['🚄', 'KTX 강릉역 도착', '09:00'],
                  ['☕', '테라로사 카페', '10:30'],
                  ['🌊', '경포 해변 산책', '14:00'],
                  ['🏨', '스테이 체크인', '17:00'],
                ] as const
              ).map(([e, t, time]) => (
                <Stack
                  key={t}
                  direction="row"
                  alignItems="center"
                  sx={{
                    bgcolor: C.paper,
                    border: `1px solid ${C.border}`,
                    borderRadius: '10px',
                    px: '12px',
                    py: '9px',
                    gap: '8px',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  <span style={{ fontSize: 16 }}>{e}</span>
                  <span style={{ color: C.ink }}>{t}</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      color: C.inkLow,
                      fontFamily: fontMono,
                      fontSize: 10,
                    }}
                  >
                    {time}
                  </span>
                </Stack>
              ))}
            </Stack>
          </StepCard>

          <StepCard
            num="STEP 03"
            title="수정·공유·재사용"
            body='"카페 하나 더 추가해줘" 한마디로 수정. PDF·링크로 공유하고, 친구의 폴리오는 Fork로 가져오기.'
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
                width: '100%',
                height: '100%',
              }}
            >
              {['📔', '🗺', '💰', '📷'].map((t) => (
                <Box
                  key={t}
                  sx={{
                    bgcolor: C.paper,
                    border: `1px solid ${C.border}`,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                  }}
                >
                  {t}
                </Box>
              ))}
            </Box>
          </StepCard>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * [목적] step 카드 공통 — num / visual / title / body
 * [이유] HTML .step
 * [사이드 이팩트] children 은 .step-visual 안쪽 렌더
 * [수정 시 고려사항] visual 영역 height(200px) 고정 — overflow:hidden 이므로 자식 콘텐츠 길이 검수 필요
 */
function StepCard({
  num,
  title,
  body,
  children,
}: {
  num: string;
  title: string;
  body: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          color: C.brand,
          fontFamily: fontMono,
          letterSpacing: '1px',
          mb: '12px',
        }}
      >
        {num}
      </Typography>
      <Box
        sx={{
          height: 200,
          bgcolor: C.bg,
          borderRadius: '14px',
          border: `1px solid ${C.border}`,
          mb: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          p: '20px',
        }}
      >
        {children}
      </Box>
      <Typography
        component="h4"
        sx={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.3px', mb: '7px', color: C.ink }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontSize: 13, color: C.inkMid, lineHeight: 1.6 }}>{body}</Typography>
    </Box>
  );
}

/**
 * [목적] step 1 시각용 미니 챗 말풍선 — bot/me 분기
 * [이유] HTML .step-chat .b / .b.me — 폰 목업 ChatBubble 보다 단순 형태
 * [사이드 이팩트] StepCard children 으로만 사용
 * [수정 시 고려사항] ChatBubble 과 별개 — 디자인 통합 시 ChatBubble 로 치환 검토
 */
function StepBubble({
  children,
  me = false,
}: {
  children: React.ReactNode;
  me?: boolean;
}): JSX.Element {
  return (
    <Box
      sx={{
        bgcolor: me ? C.brand : C.paper,
        color: me ? C.paper : C.ink,
        border: me ? 'none' : `1px solid ${C.border}`,
        borderRadius: '10px',
        px: '11px',
        py: '7px',
        fontSize: 11,
        alignSelf: me ? 'flex-end' : 'flex-start',
        maxWidth: me ? '75%' : '85%',
      }}
    >
      {children}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Folio gallery
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] 폴리오 갤러리 4카드
 * [이유] HTML <section id="gallery">
 * [사이드 이팩트] 마케팅 샘플 — 실제 사용자·폴리오가 아니므로 화면에 "샘플"임을 명시한다(QA P2-11). 가공된 작성자 핸들·
 *                좋아요 수는 실제 사용자로 오해될 수 있어 데이터에서 제거했다.
 * [수정 시 고려사항] 실 폴리오 데이터(공개 폴리오 API)로 교체할 때는 샘플 표기를 함께 제거하고 카드 클릭 → /folio/:id
 *                   navigate 를 추가. cover 그라데이션 토큰화 검토
 */
function GallerySection(): JSX.Element {
  const cards: Array<{
    cover: string;
    emoji: string;
    days: string;
    title: string;
    tags: string[];
  }> = [
    {
      cover: G.folioCover1,
      emoji: '🌊',
      days: '3일',
      title: '속초 2박3일 — 바다와 새벽 시장',
      tags: ['#바다', '#카페', '#조용한'],
    },
    {
      cover: G.folioCover2,
      emoji: '⭐',
      days: '2일',
      title: '평창 별 보러 가는 길',
      tags: ['#자연', '#캠핑', '#힐링'],
    },
    {
      cover: G.folioCover3,
      emoji: '☕',
      days: '1일',
      title: '강릉 감성 카페 6선 투어',
      tags: ['#카페', '#포토', '#MZ'],
    },
    {
      cover: G.folioCover4,
      emoji: '🏄',
      days: '2일',
      title: '양양 서핑 + 숙박 완벽 가이드',
      tags: ['#서핑', '#해안', '#액티브'],
    },
  ];
  return (
    <Box component="section" id="gallery" sx={sectionSx}>
      <Box sx={containerSx}>
        <Typography sx={sectionEyebrowSx}>GALLERY</Typography>
        <Typography component="h2" sx={sectionTitleSx}>
          이런 폴리오를 만들 수 있어요
        </Typography>
        {/* [수정: QA-P2-11] 실제 사용자가 만든 것처럼 보이던 문구 → 샘플임을 명시 */}
        <Typography sx={sectionSubSx}>서비스 이해를 돕기 위한 샘플 예시예요.</Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: '16px',
          }}
        >
          {cards.map((card) => (
            <Box
              key={card.title}
              sx={{
                bgcolor: C.paper,
                border: `1px solid ${C.border}`,
                borderRadius: '14px',
                overflow: 'hidden',
                // 2026-09-17 디자인 리뷰: 클릭 동작 없는 카드의 거짓 어포던스 제거(실데이터 연결 시 링크화)
                transition: 'transform .2s, box-shadow .2s',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: S.s3 },
              }}
            >
              <Box
                sx={{
                  height: 170,
                  background: card.cover,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 48,
                  position: 'relative',
                }}
              >
                {card.emoji}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    bgcolor: C.whiteAlpha95,
                    color: C.ink,
                    borderRadius: '6px',
                    px: '8px',
                    py: '3px',
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: fontMono,
                  }}
                >
                  {card.days}
                </Box>
              </Box>
              <Box sx={{ p: '14px 16px' }}>
                <Typography
                  component="h4"
                  sx={{
                    fontSize: 14,
                    fontWeight: 800,
                    letterSpacing: '-.3px',
                    lineHeight: 1.4,
                    mb: '9px',
                    color: C.ink,
                  }}
                >
                  {card.title}
                </Typography>
                <Stack direction="row" sx={{ gap: '4px', flexWrap: 'wrap', mb: '11px' }}>
                  {card.tags.map((t) => (
                    <Box
                      key={t}
                      sx={{
                        bgcolor: C.bgSoft,
                        color: C.inkMid,
                        borderRadius: '5px',
                        px: '7px',
                        py: '2px',
                        fontSize: 11,
                        fontWeight: 500,
                      }}
                    >
                      {t}
                    </Box>
                  ))}
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    fontSize: 11,
                    color: C.inkLow,
                    pt: '10px',
                    borderTop: `1px solid ${C.borderSoft}`,
                  }}
                >
                  {/* [수정: QA-P2-11] 가공된 작성자 핸들·좋아요 수 대신 샘플 표기 */}
                  <span>샘플 폴리오</span>
                </Stack>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Testimonials
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] 사용자 후기 3카드
 * [이유] HTML <section ... REVIEWS>
 * [사이드 이팩트] 아바타 그라데이션은 LANDING_GRADIENTS.avatar* 경유
 * [수정 시 고려사항] 후기 텍스트는 placeholder — 실 후기 도입 시 정책팀 검수(저작권·개인정보) 통과 후 교체
 *                   [2026-09-18] avatarFg — avatarGreen/avatarWarm 배경은 흰 글씨 기준 2.22/1.7:1 로
 *                   AA 미달이라 이니셜 텍스트를 ink(다크)로, avatarBrand 는 배경을 brand[600]으로 어둡혀
 *                   흰 텍스트를 유지(랜딩 tokens.ts avatarBrand 주석 참조). 아바타 추가 시 avatarFg 를
 *                   반드시 함께 지정할 것(누락 시 기본 흰색으로 AA 회귀 가능).
 */
function _TestimonialsSection(): JSX.Element {
  const quotes: Array<{
    text: string;
    avatar: string;
    avatarBg: string;
    avatarFg: string;
    name: string;
    info: string;
  }> = [
    {
      text: '"엑셀 짜던 시간이 아까울 정도. 반려견 동반 가능한 곳만 추려주는 게 진짜 편해요."',
      avatar: 'M',
      avatarBg: G.avatarBrand,
      avatarFg: C.paper,
      name: '민지님',
      info: '30대 · 반려견 동반 여행',
    },
    {
      text: '"제가 모르던 영월·인제 코스를 추천해줘서 좋았어요. 알고 보니 숨은 명소가 많더라고요."',
      avatar: 'J',
      avatarBg: G.avatarGreen,
      avatarFg: C.ink,
      name: '정훈님',
      info: '20대 · 캠핑 마니아',
    },
    {
      text: '"친구가 만든 폴리오를 그대로 가져와서 살짝 수정. 일정 짜기 너무 편하네요."',
      avatar: 'S',
      avatarBg: G.avatarWarm,
      avatarFg: C.ink,
      name: '서연님',
      info: '20대 · MZ 그룹 여행',
    },
  ];
  return (
    <Box
      component="section"
      sx={{
        ...sectionSx,
        bgcolor: C.paper,
        borderTop: `1px solid ${C.borderSoft}`,
        borderBottom: `1px solid ${C.borderSoft}`,
      }}
    >
      <Box sx={containerSx}>
        <Typography sx={sectionEyebrowSx}>REVIEWS</Typography>
        <Typography component="h2" sx={sectionTitleSx}>
          사용자들의 한 마디
        </Typography>
        <Typography sx={sectionSubSx}>베타 기간 동안 받은 진짜 후기들.</Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: '14px', md: '20px' },
          }}
        >
          {quotes.map((q) => (
            <Box
              key={q.name}
              sx={{
                bgcolor: C.paper,
                border: `1px solid ${C.border}`,
                borderRadius: '14px',
                p: '24px',
              }}
            >
              {/* [목적] 별점 표시 — 색만으로 정보 전달 금지(WCAG 1.4.1)
                  [이유] warm(#F2A65A) 별 아이콘은 흰 배경 위 2.02:1 로 AA 미달이었고, 별 문자만으로는
                  스크린리더 사용자에게 정확한 점수가 전달되지 않는다.
                  [사이드 이팩트] 별 아이콘은 aria-hidden 으로 장식화하고, 인접 수치 텍스트(aria-label 겸용)를
                  실제 접근 가능한 정보원으로 둔다. 색은 warmStrong(brand warm[600], 4.81:1)으로 상향.
                  [수정 시 고려사항] 후기가 실 데이터로 교체되면 5.0 하드코딩을 실제 평점 필드로 교체할 것. */}
              <Stack direction="row" alignItems="center" spacing="6px" sx={{ mb: '12px' }}>
                <Box
                  aria-hidden="true"
                  sx={{ color: C.warmStrong, fontSize: 14, letterSpacing: '2px' }}
                >
                  ★★★★★
                </Box>
                <Typography
                  component="span"
                  aria-label="평점 5점 만점에 5점"
                  sx={{ fontSize: 11, fontWeight: 700, color: C.inkMid }}
                >
                  5.0
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: 1.65,
                  color: C.ink,
                  mb: '16px',
                  fontWeight: 500,
                }}
              >
                {q.text}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                sx={{ gap: '10px', pt: '14px', borderTop: `1px solid ${C.borderSoft}` }}
              >
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    background: q.avatarBg,
                    color: q.avatarFg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: 13,
                  }}
                >
                  {q.avatar}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.ink }}>
                    {q.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: C.inkLow, fontFamily: fontMono }}>
                    {q.info}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] FAQ — 6 항목, 첫 항목 default open
 * [이유] HTML <details class="faq-item">
 * [사이드 이팩트] button+aria-expanded 아코디언 — MUI Accordion 미도입 (번들 절약). 열림 상태는 컴포넌트 로컬 state
 * [수정 시 고려사항] 항목 추가 시 open 플래그는 첫 렌더 기본 열림 여부만 결정. '+' 회전은 열림 상태로 제어(CSS details 아님)
 */
function FaqSection(): JSX.Element {
  const items: Array<{ q: string; a: string; open?: boolean }> = [
    {
      q: '강원캔버스는 무료인가요?',
      a: '네, 모든 핵심 기능을 무료로 이용하실 수 있어요. 광고 없이, 일정 생성·수정·공유 모두 무료입니다. 추후 프리미엄 기능(고급 PDF 디자인 등)은 별도로 안내드릴 예정이에요.',
      open: true,
    },
    {
      q: '강원도 외 지역은 언제 추가되나요?',
      a: 'v1.0은 강원 18개 시·군에 집중합니다. 2026 상반기에 충남·부산·제주가 추가될 예정이며, 사용자 피드백을 받아 점진적으로 확장합니다.',
    },
    {
      q: 'AI가 추천한 장소가 폐업했을 때는요?',
      a: '한국관광공사 TourAPI 원본 데이터를 야간 배치로 동기화하며, 폐업·정보 오류 신고 기능을 통해 사용자가 직접 알릴 수 있어요. 자동 가시성 조정 후 운영자가 검토합니다.',
    },
    {
      q: '반려견 동반 정보는 어떻게 검증되나요?',
      a: '한국관광공사의 useKorPetTour 공식 데이터를 1차 소스로 사용하고, 사용자 리뷰로 보정합니다. "실내 동반 가능 / 야외만 가능 / 케이지 필요" 등 세부 조건도 표기해드려요.',
    },
    {
      q: '개인정보는 어떻게 보호되나요?',
      a: '카카오·네이버·Google OAuth 로그인만 사용하며, 비밀번호를 별도로 저장하지 않습니다. 위치 정보는 광역(시·군) 단위만 수집하고, 정확한 GPS는 사용자 동의 시에만 일시적으로 사용합니다.',
    },
    {
      q: 'PC에서도 사용할 수 있나요?',
      a: '네, 강원캔버스는 웹과 앱이 하나의 반응형 서비스예요. 다운로드 없이 브라우저에서 바로 이용 가능하고, 큰 화면에서는 좌측 채팅 / 우측 폴리오 미리보기로 더 넓은 작업 공간을 활용합니다. 계정은 웹·앱 어디서도 자동 동기화돼요.',
    },
  ];
  // [수정: QA-P3-01] 열림 상태를 명시적으로 들고 button+aria-expanded 로 노출한다 — 여러 항목을 동시에 열 수 있고(details 와
  //   동일) 첫 항목은 기본 열림. 네이티브 summary 에 display:flex 를 주면 브라우저/스크린리더에 따라 disclosure 역할이
  //   흐려질 수 있어, 상태(aria-expanded)·연결(aria-controls)·영역(role=region)을 직접 선언해 보조기술 전달을 보장한다.
  const [openIdx, setOpenIdx] = useState<ReadonlySet<number>>(
    () => new Set(items.flatMap((it, i) => (it.open ? [i] : []))),
  );
  const toggle = (i: number): void =>
    setOpenIdx((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  return (
    <Box component="section" id="faq" sx={sectionSx}>
      <Box sx={containerSx}>
        <Typography sx={sectionEyebrowSx}>FAQ</Typography>
        <Typography component="h2" sx={sectionTitleSx}>
          자주 묻는 질문
        </Typography>
        <Typography sx={{ ...sectionSubSx, mb: '48px' }}>
          더 궁금한 점은 고객센터로 문의해주세요.
        </Typography>

        <Box sx={{ maxWidth: '720px', mx: 'auto' }}>
          {items.map((it, i) => {
            const isOpen = openIdx.has(i);
            const buttonId = `faq-q-${i}`;
            const panelId = `faq-a-${i}`;
            return (
              <Box
                key={it.q}
                sx={{
                  bgcolor: C.paper,
                  border: `1px solid ${C.border}`,
                  borderRadius: '12px',
                  mb: '8px',
                  overflow: 'hidden',
                }}
              >
                <Typography
                  component="h3"
                  sx={{ m: 0, fontSize: 'inherit', fontWeight: 'inherit' }}
                >
                  <ButtonBase
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggle(i)}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      textAlign: 'left',
                      px: '22px',
                      py: '18px',
                      fontWeight: 700,
                      fontSize: 14,
                      letterSpacing: '-.2px',
                      color: C.ink,
                      fontFamily: 'inherit',
                      '&:focus-visible': { outline: `2px solid ${C.brand}`, outlineOffset: '-2px' },
                    }}
                  >
                    <span>{it.q}</span>
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        color: C.inkLow,
                        fontSize: 18,
                        fontWeight: 400,
                        transition: 'transform .2s',
                        transform: isOpen ? 'rotate(45deg)' : 'none',
                      }}
                    >
                      +
                    </Box>
                  </ButtonBase>
                </Typography>
                <Box id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>
                  <Typography
                    sx={{
                      px: '22px',
                      pb: '18px',
                      fontSize: 13.5,
                      lineHeight: 1.7,
                      color: C.inkMid,
                    }}
                  >
                    {it.a}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Final CTA
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] 페이지 하단 그라데이션 CTA 배너 — '웹에서 시작' primary + 스토어 버튼 2개
 * [이유] HTML <section class="final-cta">
 * [사이드 이팩트] primary 클릭 → /login. 스토어 링크는 v1.0 미발행 → '#' 유지 (anchor 가드)
 * [수정 시 고려사항] 앱 스토어 실 URL 결정 시 useNavigate 가 아닌 외부 링크 → target=_blank rel="noopener"
 */
function FinalCtaSection(): JSX.Element {
  const navigate = useNavigate();
  return (
    <Box component="section" sx={{ py: 0 }}>
      <Box sx={{ ...containerSx, px: 0 }}>
        <Box
          sx={{
            background: G.finalCta,
            color: C.paper,
            borderRadius: '28px',
            px: { xs: '24px', md: '40px' },
            py: { xs: '56px', md: '80px' },
            textAlign: 'center',
            mx: { xs: '16px', md: '24px' },
            my: { xs: '40px', md: '60px' },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-30%',
              width: 600,
              height: 600,
              borderRadius: '50%',
              background: G.finalCtaGlow,
              pointerEvents: 'none',
            },
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: 'clamp(28px, 4vw, 44px)',
              fontWeight: 800,
              letterSpacing: '-1px',
              mb: '14px',
              position: 'relative',
            }}
          >
            강원, 어떻게 그려볼까요?
          </Typography>
          <Typography
            sx={{
              fontSize: 16,
              // [2026-09-18 대비 상향] 그라데이션 위 흰 텍스트 알파(.9)는 추가 저하 요인 → solid
              mb: '32px',
              maxWidth: '520px',
              mx: 'auto',
              position: 'relative',
              lineHeight: 1.65,
            }}
          >
            다운로드 없이 지금 바로 시작하세요. 웹에서도 앱과 동일하게 작동해요.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: '22px', position: 'relative' }}>
            <ButtonBase
              onClick={() => navigate('/login')}
              aria-label="웹에서 시작 (PWA)"
              sx={{
                bgcolor: C.paper,
                color: C.brand,
                px: '36px',
                py: '18px',
                borderRadius: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '14px',
                boxShadow: C.ctaShadow,
              }}
            >
              <span style={{ fontSize: 26 }}>→</span>
              <span style={{ textAlign: 'left', lineHeight: 1.15 }}>
                <Box
                  component="small"
                  sx={{ fontSize: 11, opacity: 0.65, display: 'block', fontWeight: 600 }}
                >
                  바로 이용하기
                </Box>
                <Box
                  component="strong"
                  sx={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.3px' }}
                >
                  웹에서 시작 (PWA)
                </Box>
              </span>
            </ButtonBase>
          </Box>
          {/* [수정: 디자인 개편] 실제 출시되지 않은 앱스토어/구글플레이 배지(href="#" placeholder)를 제거 —
              검증되지 않은 다운로드 배지를 보여주는 것은 사실이 아니므로 제거하고, 웹에서 바로 시작하는
              기존 CTA 버튼만 남겨둔다. */}
        </Box>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] 하단 푸터 — 브랜드 / 서비스 / 회사 / 지원 4컬럼 + bottom bar
 * [이유] HTML <footer>
 * [사이드 이팩트] '웹 앱 시작' 만 /login navigate. 연결할 URL 이 없는 링크(회사 소개·블로그·채용·파트너십·SNS·위치정보 이용약관)는
 *                목적지 없는 '#' 로 두지 않고 제거했다(QA P2-10). 고객센터는 공개 라우트 /settings/help 로 연결.
 * [수정 시 고려사항] 이용약관/개인정보는 /settings/tos · /settings/privacy 공개 라우트 직결 — 라우트 변경 시 동기화
 */
function LandingFooter(): JSX.Element {
  const navigate = useNavigate();
  type Col = { h: string; links: Array<{ label: string; href?: string; onClick?: () => void }> };
  const cols: Col[] = [
    {
      h: '서비스',
      links: [
        { label: '기능 소개', href: '#features' },
        { label: '사용법', href: '#how' },
        { label: '폴리오 갤러리', href: '#gallery' },
        { label: '웹 앱 시작', onClick: () => navigate('/login') },
      ],
    },
    {
      h: '지원',
      links: [
        { label: '고객센터', href: '/settings/help' },
        { label: '이용약관', href: '/settings/tos' },
        { label: '개인정보 처리방침', href: '/settings/privacy' },
      ],
    },
  ];
  return (
    <Box
      component="footer"
      sx={{ bgcolor: C.paper, borderTop: `1px solid ${C.borderSoft}`, pt: '56px', pb: '32px' }}
    >
      <Box sx={containerSx}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr 1fr' },
            gap: { xs: '28px', md: '40px' },
            mb: '40px',
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '9px',
                  background: G.logoMark,
                  color: C.paper,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: 14,
                  letterSpacing: '-.5px',
                }}
              >
                강
              </Box>
              <Typography
                sx={{ fontSize: 15, fontWeight: 800, letterSpacing: '-.4px', color: C.ink }}
              >
                강원캔버스
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: 13, color: C.inkMid, lineHeight: 1.6, mt: '12px', maxWidth: '320px' }}
            >
              대화로 그리는 강원 여행 폴리오. 한국관광공사 TourAPI 데이터를 활용한 AI 여행 도우미.
            </Typography>
          </Box>

          {cols.map((col) => (
            <Box key={col.h}>
              <Typography
                component="h5"
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: C.inkLow,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  mb: '14px',
                }}
              >
                {col.h}
              </Typography>
              {col.links.map((l) => {
                if (l.onClick) {
                  return (
                    <Box
                      key={l.label}
                      component="button"
                      onClick={l.onClick}
                      sx={{
                        display: 'block',
                        fontSize: 13,
                        color: C.inkMid,
                        py: '5px',
                        transition: 'color .15s',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        p: '5px 0',
                        '&:hover': { color: C.ink },
                      }}
                    >
                      {l.label}
                    </Box>
                  );
                }
                if (l.href && l.href.startsWith('/')) {
                  return (
                    <Box
                      key={l.label}
                      component={RouterLink}
                      to={l.href}
                      sx={{
                        display: 'block',
                        fontSize: 13,
                        color: C.inkMid,
                        py: '5px',
                        transition: 'color .15s',
                        textDecoration: 'none',
                        '&:hover': { color: C.ink },
                      }}
                    >
                      {l.label}
                    </Box>
                  );
                }
                return (
                  <Box
                    key={l.label}
                    component="a"
                    href={l.href}
                    sx={{
                      display: 'block',
                      fontSize: 13,
                      color: C.inkMid,
                      py: '5px',
                      transition: 'color .15s',
                      '&:hover': { color: C.ink },
                    }}
                  >
                    {l.label}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          sx={{
            pt: '24px',
            borderTop: `1px solid ${C.borderSoft}`,
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            fontSize: 12,
            color: C.inkLow,
          }}
        >
          <Box>© 2026 GangwonCanvas Team. All rights reserved.</Box>
        </Stack>
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page entry
// ─────────────────────────────────────────────────────────────────────────────

/**
 * [목적] LandingPage 진입 — RootSwitch 가 guest 상태일 때 / 라우트에 렌더
 * [이유] App 셸 우회 + 자체 Header/Footer 풀 페이지. 시각 구성은 docs/플리오랩/강원캔버스-웹.html 1:1 매핑
 * [사이드 이팩트] body bgcolor 영향(C.bg) — RootSwitch 외부에서 호출 시 페이지 외 영역 색차 가능
 *                 GlobalStyles 로 landing-float keyframes 주입 — 다른 페이지에 영향 없음
 * [수정 시 고려사항] 시각 회귀 우선. 섹션 추가/삭제 시 anchor id 와 푸터 링크 동기화 필요
 */
export function LandingPage(): JSX.Element {
  return (
    <Box
      sx={{
        bgcolor: C.bg,
        color: C.ink,
        fontFamily: tokens.typography.fontFamily.sans,
        lineHeight: 1.55,
      }}
    >
      <GlobalStyles
        styles={{
          html: { scrollBehavior: 'smooth' },
        }}
      />
      <style>{K.float}</style>
      <LandingHeader />
      <HeroSection />
      <TrustSection />
      <FeaturesSection />
      <HowSection />
      <GallerySection />
      {/* [수정: 디자인 개편] 검증되지 않은 후기(placeholder 문구/가상 사용자)를 녹인 REVIEWS 섹션을 랜딩에서 제거한다.
          _TestimonialsSection 함수 자체는 후속에 실제 후기가 생기면 재사용할 수 있도록 남겨둔다.
          미사용 상태라 no-unused-vars 를 통과하려고 `_` 접두어를 붙였다(재사용 시 접두어 제거). */}
      <FaqSection />
      <FinalCtaSection />
      <LandingFooter />
    </Box>
  );
}
